// =============================================================================
// ui/tabs/SaveTemplateDialog.tsx — naming a template captured from an account.
// -----------------------------------------------------------------------------
// The old client asks for the name in a bare prompt box. This one shows the
// SUMMARY that is about to be stored beside it, for one reason: a template is
// picked months later from a one-line label, and a label that turns out not to
// describe the numbers under it is the failure mode the whole feature has.
//
// ── 🚨 WHAT THE COPY MAY NOT SAY (D127) ──
//
// No surface may state or imply that TRADECOACH tracks, audits or verifies a
// prop firm's rules. The old client REMOVED per-firm templates because a stale
// copy feeds the drawdown floor and hands the trader more room than they have.
// What replaced them is this: the trader's OWN numbers, stamped with when they
// saved them. The copy below says whose numbers they are, in those words.
//
// ── ⚠ A MATCHING NAME REPLACES, AND THE DIALOG SAYS SO BEFORE YOU PRESS SAVE ──
//
// Re-saving from the same account is the documented way to update a template.
// In the old client that appends a second row with the same name, so the picker
// ends up with two entries no human can tell apart. Here it replaces — which is
// only safe if the button admits it, so the button text itself changes.
// =============================================================================

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { templateSaveDescription, type PropTemplate } from '../../selectors/accountSetup';
import type { AccountRules } from '../../selectors/accounts';

export interface SaveTemplateDialogProps {
  /** The account being captured — for the heading and the preview. */
  accountName: string;
  firm: string;
  accountSize: number;
  rules: Partial<AccountRules> | null;
  /** What the box opens with. */
  suggested: string;
  /** Everything already stored, so a name clash can be named before it happens. */
  existing: readonly PropTemplate[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (label: string) => void;
}

export function SaveTemplateDialog(
  { accountName, firm, accountSize, rules, suggested, existing, busy, error, onCancel, onSave }:
  SaveTemplateDialogProps,
) {
  const [label, setLabel] = useState(suggested);

  const trimmed = label.trim();
  // ⚠ Case-folded, because that is what the storage layer matches on. A dialog
  // that promised "new" and then replaced would be worse than no warning.
  const clash = existing.find(
    (t) => t.label.trim().toLowerCase() === trimmed.toLowerCase(),
  ) ?? null;

  // 🚨 NOT the stored summary — that one is silent about the daily loss limit,
  // which on a SIM or personal account is often the only rule there is. See
  // `templateSaveDescription`; found by opening this dialog on Josh's Sim1.
  const summary = templateSaveDescription(rules, accountSize);

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Save rules as template">
      <div className="modal">
        <header className="modal-head">
          <b>Save “{accountName}”’s rules as a template</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body stpl-body">
          <label className="stpl-fld">
            <span className="stpl-lbl">Template name</span>
            <input
              value={label}
              autoFocus
              maxLength={40}
              placeholder="e.g. Apex 100K"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && trimmed && !busy) onSave(trimmed);
              }}
            />
          </label>

          {/* ⚠ THE NUMBERS, NOT JUST THE NAME. This is what will be prefilled
              into the next account, and the label is all that is visible later. */}
          <div className="stpl-preview">
            <span className="stpl-preview-k">Saves</span>
            <span className="stpl-preview-v">
              {summary}{firm ? ` · ${firm}` : ''}
            </span>
          </div>

          {clash ? (
            <p className="stpl-clash">
              You already have a template called <b>“{clash.label}”</b>. Saving will{' '}
              <b>replace it</b> with these numbers — accounts you already created
              from it keep the rules they were given.
            </p>
          ) : null}

          {/* 🚨 D127. Never imply we track the firm's rules. */}
          <p className="muted stpl-note">
            These are <b>your</b> numbers, copied from this account — not{' '}
            {firm || 'the firm'}’s current rules. The template is stamped with
            today’s date so you can see how old it is when you use it, and
            you should check it against your firm’s own page before trusting the
            gauges on an account built from it.
          </p>

          {error ? <p className="red stpl-err">{error}</p> : null}
        </div>

        <footer className="modal-foot">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <div className="grow" />
          <button
            type="button" className="btn primary"
            disabled={busy || !trimmed}
            onClick={() => onSave(trimmed)}
          >
            {busy
              ? 'Saving…'
              : clash ? 'Replace template' : 'Save template'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
